import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Calendar,
  Building2,
  User,
  DollarSign,
  Clock,
  MapPin,
  Users,
  FileText,
  Phone,
  Mail,
  Package,
  AlertCircle,
  CheckCircle,
  XCircle,
  Search
} from "lucide-react";
import type { BookingOptionResponse } from "@/types/activities";
import { getBookingOptionStatusColor } from "@/types/activities";

interface BookingOptionDetailDrawerProps {
  option: BookingOptionResponse | null;
  open: boolean;
  onClose: () => void;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'Available':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'Unavailable':
    case 'Rejected':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'Approved':
      return <CheckCircle className="w-4 h-4 text-blue-500" />;
    case 'Exploring':
      return <Search className="w-4 h-4 text-yellow-500" />;
    default:
      return <AlertCircle className="w-4 h-4 text-gray-500" />;
  }
};

export default function BookingOptionDetailDrawer({
  option,
  open,
  onClose
}: BookingOptionDetailDrawerProps) {
  if (!option) return null;

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (price?: number, currency: string = 'AED') => {
    if (!price) return "Not specified";
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency
    }).format(price);
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl">
        <SheetHeader className="pb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-lg">
                  {option.serviceOptionName || 'Booking Option'}
                </SheetTitle>
                <SheetDescription className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {option.vendorName || 'Vendor'}
                  <span className="text-muted-foreground">•</span>
                  Option #{option.optionOrder}
                </SheetDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(option.status)}
              <Badge
                variant="outline"
                className={`${getBookingOptionStatusColor(option.status)} text-xs`}
              >
                {option.status}
              </Badge>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="h-full pr-6">
          <div className="space-y-6">

            {/* Service Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Service Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Service</p>
                    <p className="font-medium">{option.serviceName || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Service Option</p>
                    <p className="font-medium">{option.serviceOptionName || 'Not specified'}</p>
                  </div>
                  {option.marketplacePrice && (
                    <div>
                      <p className="text-muted-foreground">Marketplace Price</p>
                      <p className="font-medium">{formatPrice(option.marketplacePrice)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Vendor Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Vendor Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-muted-foreground text-xs">Vendor Name</p>
                    <p className="font-medium">{option.vendorName || 'Not specified'}</p>
                  </div>

                  {option.vendorContactEmail && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <a
                        href={`mailto:${option.vendorContactEmail}`}
                        className="text-sm text-primary hover:underline"
                      >
                        {option.vendorContactEmail}
                      </a>
                    </div>
                  )}

                  {option.vendorContactPhone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a
                        href={`tel:${option.vendorContactPhone}`}
                        className="text-sm text-primary hover:underline"
                      >
                        {option.vendorContactPhone}
                      </a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Vendor Response */}
            {option.status === 'Available' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    Vendor Available - Proposal Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Proposed Price</p>
                      <p className="font-medium text-green-700">{formatPrice(option.proposedPrice)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Proposed Capacity</p>
                      <p className="font-medium">{option.proposedCapacity ? `${option.proposedCapacity} people` : 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Start Date</p>
                      <p className="font-medium">{formatDate(option.proposedStartDate)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">End Date</p>
                      <p className="font-medium">{formatDate(option.proposedEndDate)}</p>
                    </div>
                  </div>

                  {option.proposedLocation && (
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">Proposed Location</p>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <p className="text-sm">{option.proposedLocation}</p>
                      </div>
                    </div>
                  )}

                  {option.specialTerms && (
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">Special Terms & Conditions</p>
                      <div className="bg-muted p-3 rounded-md">
                        <p className="text-sm">{option.specialTerms}</p>
                      </div>
                    </div>
                  )}

                  {option.cancellationPolicy && (
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">Cancellation Policy</p>
                      <div className="bg-muted p-3 rounded-md">
                        <p className="text-sm">{option.cancellationPolicy}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Unavailable Reason */}
            {option.status === 'Unavailable' && option.unavailableReason && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                    <XCircle className="w-4 h-4" />
                    Vendor Unavailable
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-red-50 border border-red-200 p-3 rounded-md">
                    <p className="text-sm text-red-800">{option.unavailableReason}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Organization Counter-Offer */}
            {option.status === 'Rejected' && (option.counterStartDate || option.counterEndDate || option.counterCapacity) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2 text-orange-700">
                    <AlertCircle className="w-4 h-4" />
                    Organization Counter-Offer
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {option.counterStartDate && (
                      <div>
                        <p className="text-muted-foreground">Counter Start Date</p>
                        <p className="font-medium">{formatDate(option.counterStartDate)}</p>
                      </div>
                    )}
                    {option.counterEndDate && (
                      <div>
                        <p className="text-muted-foreground">Counter End Date</p>
                        <p className="font-medium">{formatDate(option.counterEndDate)}</p>
                      </div>
                    )}
                    {option.counterCapacity && (
                      <div>
                        <p className="text-muted-foreground">Counter Capacity</p>
                        <p className="font-medium">{option.counterCapacity} people</p>
                      </div>
                    )}
                  </div>
                  {option.respondedByEmployeeName && (
                    <p className="text-xs text-muted-foreground">
                      Countered by: {option.respondedByEmployeeName}
                    </p>
                  )}
                  <div className="bg-orange-50 border border-orange-200 p-3 rounded-md">
                    <p className="text-xs text-orange-800">
                      <strong>Action Required:</strong> Organization countered the proposed dates/capacity. Create a new booking option after negotiating with vendor.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Organization Rejection */}
            {option.status === 'Rejected' && option.rejectionReason && !option.counterStartDate && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                    <XCircle className="w-4 h-4" />
                    Organization Rejection
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="bg-red-50 border border-red-200 p-3 rounded-md">
                    <p className="text-sm text-red-800 mb-2"><strong>Rejection Reason:</strong></p>
                    <p className="text-sm text-red-800">{option.rejectionReason}</p>
                  </div>
                  {option.respondedByEmployeeName && (
                    <p className="text-xs text-muted-foreground">
                      Rejected by: {option.respondedByEmployeeName}
                    </p>
                  )}
                  <div className="bg-red-50 border border-red-200 p-3 rounded-md">
                    <p className="text-xs text-red-800">
                      <strong>Action Required:</strong> Explore alternatives or create new booking option with different vendor/service.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Created</span>
                  <span className="font-medium">{formatDateTime(option.createdAt)}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Last Updated</span>
                  <span className="font-medium">{formatDateTime(option.updatedAt)}</span>
                </div>
              </CardContent>
            </Card>

            {/* System Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  System Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Option ID</p>
                    <p className="font-mono text-xs bg-muted px-2 py-1 rounded">{option.id}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Booking ID</p>
                    <p className="font-mono text-xs bg-muted px-2 py-1 rounded">{option.bookingId}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Vendor ID</p>
                    <p className="font-mono text-xs bg-muted px-2 py-1 rounded">{option.vendorId}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Service ID</p>
                    <p className="font-mono text-xs bg-muted px-2 py-1 rounded">{option.serviceId}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}